import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { ErrorBoundary } from 'react-error-boundary';
import App from './App';
import { AuthProvider } from './hooks/use-auth';
import { applyInitialTheme, useTheme } from './hooks/use-theme';
import './styles/index.css';
import { startSessionKeeper } from './lib/api';

// Apply theme BEFORE React mounts so we never flash the wrong palette
applyInitialTheme();
startSessionKeeper();

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors closeButton />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

function FatalError({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-bg text-muted-strong">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-2xl font-semibold text-accent-400">Something broke</h1>
        <p className="text-sm text-muted">{error.message}</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-full bg-accent-600 text-white text-sm">
          Reload
        </button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={FatalError}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <App />
            <ThemedToaster />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
